import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaUsers, 
  FaPlus, 
  FaSearch, 
  FaFilter,
  FaEye,
  FaEdit,
  FaTrash,
  FaDownload,
  FaUserPlus,
  FaTimes,
  FaSave,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaIdCard,
  FaCalendarAlt,
  FaCheck,
  FaExclamationTriangle
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
          color: white;
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(246, 139, 33, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover {
            background: #c82333;
            transform: translateY(-2px);
          }
        `;
      default:
        return `
          background: white;
          color: #141340;
          border: 2px solid #e0e0e0;
          &:hover {
            border-color: #F68B21;
            color: #F68B21;
          }
        `;
    }
  }}
`;

const AlertMessage = styled.div<{ type: 'success' | 'error' | 'warning' }>`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ type }) => {
    switch (type) {
      case 'success':
        return `
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;
      case 'error':
        return `
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;
      case 'warning':
        return `
          background: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
        `;
    }
  }}
`;

const SearchAndFilters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
  
  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 1rem;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #F68B21;
      box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
    }
  }
  
  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  th {
    background: #f8f9fa;
    color: #141340;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  tr:hover {
    background: #f8f9fa;
  }
  
  td {
    color: #333;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${({ status }) => {
    switch (status) {
      case 'active':
        return `
          background: #d4edda;
          color: #155724;
        `;
      case 'inactive':
        return `
          background: #f8d7da;
          color: #721c24;
        `;
      case 'pending':
        return `
          background: #fff3cd;
          color: #856404;
        `;
      default:
        return `
          background: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

const ActionButtonsCell = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{ variant: 'view' | 'edit' | 'delete' | 'assign' }>`
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${({ variant }) => {
    switch (variant) {
      case 'view':
        return `
          background: #e3f2fd;
          color: #1976d2;
          &:hover {
            background: #bbdefb;
            transform: scale(1.1);
          }
        `;
      case 'edit':
        return `
          background: #fff3e0;
          color: #f57c00;
          &:hover {
            background: #ffe0b2;
            transform: scale(1.1);
          }
        `;
      case 'delete':
        return `
          background: #ffebee;
          color: #d32f2f;
          &:hover {
            background: #ffcdd2;
            transform: scale(1.1);
          }
        `;
      case 'assign':
        return `
          background: #e8f5e8;
          color: #2e7d32;
          &:hover {
            background: #c8e6c9;
            transform: scale(1.1);
          }
        `;
    }
  }}
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
`;

const PaginationInfo = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #e0e0e0;
  background: ${({ active }) => (active ? '#F68B21' : 'white')};
  color: ${({ active }) => (active ? 'white' : '#141340')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ active }) => (active ? '#e67c1a' : '#f8f9fa')};
  }
`;

// Modal Styles
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  color: #141340;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f0f0f0;
    color: #141340;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #141340;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #F68B21;
    box-shadow: 0 0 0 3px rgba(246, 139, 33, 0.1);
  }
`;

const PasswordStrength = styled.div<{ strength: 'weak' | 'medium' | 'strong' }>`
  font-size: 0.8rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  ${({ strength }) => {
    switch (strength) {
      case 'weak':
        return `color: #dc3545;`;
      case 'medium':
        return `color: #ffc107;`;
      case 'strong':
        return `color: #28a745;`;
      default:
        return `color: #6c757d;`;
    }
  }}
`;

const PasswordMatch = styled.div<{ matches: boolean }>`
  font-size: 0.8rem;
  margin-top: 0.25rem;
  color: ${({ matches }) => (matches ? '#28a745' : '#dc3545')};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 2px solid #f0f0f0;
`;

const HRStaffList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error' | 'warning', message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const itemsPerPage = 10;

  // Form state for new staff
  const [newStaff, setNewStaff] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    role: '',
    emergencyContact: '',
    emergencyName: '',
    joinDate: '',
    status: 'active',
    password: '',
    confirmPassword: ''
  });

  // Mock data for staff - using valid system roles
  const [staffData, setStaffData] = useState([
    {
      id: 1,
      name: 'John Doe',
      email: 'john.doe@example.com',
      role: 'Supervisor',
      status: 'active',
      joinDate: '2023-01-15',
      phone: '+255 123 456 789',
      address: 'Dar es Salaam, Tanzania',
      assignedProjects: ['PRJ-001', 'PRJ-003']
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah.johnson@example.com',
      role: 'Accountant',
      status: 'active',
      joinDate: '2023-02-20',
      phone: '+255 123 456 790',
      address: 'Arusha, Tanzania',
      assignedProjects: ['PRJ-002']
    },
    {
      id: 3,
      name: 'Michael Smith',
      email: 'michael.smith@example.com',
      role: 'Project Manager',
      status: 'active',
      joinDate: '2022-11-10',
      phone: '+255 123 456 791',
      address: 'Mwanza, Tanzania',
      assignedProjects: []
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily.davis@example.com',
      role: 'Store-Keeper (Logistics)',
      status: 'active',
      joinDate: '2023-03-05',
      phone: '+255 123 456 792',
      address: 'Dodoma, Tanzania',
      assignedProjects: ['PRJ-001', 'PRJ-004']
    },
    {
      id: 5,
      name: 'David Wilson',
      email: 'david.wilson@example.com',
      role: 'Cashier',
      status: 'active',
      joinDate: '2023-04-12',
      phone: '+255 123 456 793',
      address: 'Tanga, Tanzania',
      assignedProjects: []
    },
    {
      id: 6,
      name: 'Lisa Anderson',
      email: 'lisa.anderson@example.com',
      role: 'Procurement',
      status: 'inactive',
      joinDate: '2023-05-01',
      phone: '+255 123 456 794',
      address: 'Zanzibar, Tanzania',
      assignedProjects: []
    }
  ]);

  // Mock projects for assignment
  const projects = [
    { id: 'PRJ-001', name: 'Road Construction Project' },
    { id: 'PRJ-002', name: 'School Renovation' },
    { id: 'PRJ-003', name: 'Water Supply System' },
    { id: 'PRJ-004', name: 'Healthcare Center' },
    { id: 'PRJ-005', name: 'Agricultural Training' }
  ];

  // Valid system roles
  const validRoles = [
    'Supervisor',
    'Store-Keeper (Logistics)',
    'Cashier',
    'Procurement',
    'Accountant',
    'Project Manager'
  ];

  // Filter and search logic
  const filteredStaff = staffData.filter(staff => {
    const matchesSearch = staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         staff.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || staff.status === statusFilter;
    const matchesRole = roleFilter === 'all' || staff.role === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredStaff.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStaff = filteredStaff.slice(startIndex, endIndex);

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Password validation
    if (newStaff.password !== newStaff.confirmPassword) {
      setAlertMessage({ type: 'error', message: 'Passwords do not match. Please try again.' });
      setIsSubmitting(false);
      return;
    }
    
    if (newStaff.password.length < 6) {
      setAlertMessage({ type: 'error', message: 'Password must be at least 6 characters long.' });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const newStaffMember = {
        id: staffData.length + 1,
        ...newStaff,
        assignedProjects: []
      };
      
      setStaffData([...staffData, newStaffMember]);
      setAlertMessage({ type: 'success', message: 'Staff member added successfully! Login credentials have been sent to their email.' });
      setIsAddModalOpen(false);
      setNewStaff({
        name: '',
        email: '',
        phone: '',
        address: '',
        role: '',
        emergencyContact: '',
        emergencyName: '',
        joinDate: '',
        status: 'active',
        password: '',
        confirmPassword: ''
      });
      
      // Clear alert after 3 seconds
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      setAlertMessage({ type: 'error', message: 'Failed to add staff member. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setStaffData(staffData.map(staff => 
        staff.id === selectedStaff.id ? selectedStaff : staff
      ));
      
      setAlertMessage({ type: 'success', message: 'Staff member updated successfully!' });
      setIsEditModalOpen(false);
      setSelectedStaff(null);
      
      // Clear alert after 3 seconds
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      setAlertMessage({ type: 'error', message: 'Failed to update staff member. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStaff = async (staffId: number) => {
    if (window.confirm('Are you sure you want to delete this staff member? This action cannot be undone.')) {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setStaffData(staffData.filter(staff => staff.id !== staffId));
        setAlertMessage({ type: 'success', message: 'Staff member deleted successfully.' });
        
        // Clear alert after 3 seconds
        setTimeout(() => setAlertMessage(null), 3000);
      } catch (error) {
        setAlertMessage({ type: 'error', message: 'Failed to delete staff member. Please try again.' });
      }
    }
  };

  const handleAssignProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setAlertMessage({ type: 'success', message: `${selectedStaff.name} has been successfully assigned to the project.` });
      setIsAssignModalOpen(false);
      setSelectedStaff(null);
      
      // Clear alert after 3 seconds
      setTimeout(() => setAlertMessage(null), 3000);
    } catch (error) {
      setAlertMessage({ type: 'error', message: 'Failed to assign staff to project. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (staff: any) => {
    setSelectedStaff({...staff});
    setIsEditModalOpen(true);
  };

  const openAssignModal = (staff: any) => {
    setSelectedStaff({...staff});
    setIsAssignModalOpen(true);
  };

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0
    }).format(parseInt(amount) || 0);
  };

  const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
    if (password.length < 6) return 'weak';
    if (password.length < 8) return 'medium';
    return 'strong';
  };

  const getPasswordStrengthText = (strength: 'weak' | 'medium' | 'strong') => {
    switch (strength) {
      case 'weak':
        return 'Weak password';
      case 'medium':
        return 'Medium strength password';
      case 'strong':
        return 'Strong password';
      default:
        return '';
    }
  };

  return (
    <Container>
      {alertMessage && (
        <AlertMessage type={alertMessage.type}>
          <IconWrapper icon={alertMessage.type === 'success' ? FaCheck : FaExclamationTriangle} />
          {alertMessage.message}
        </AlertMessage>
      )}

      <PageHeader>
        <PageTitle>
          <IconWrapper icon={FaUsers} />
          Staff Management
        </PageTitle>
        <ActionButtons>
          <Button variant="secondary">
            <IconWrapper icon={FaDownload} />
            Export
          </Button>
          <Button variant="primary" onClick={() => setIsAddModalOpen(true)}>
            <IconWrapper icon={FaUserPlus} />
            Add New Staff
          </Button>
        </ActionButtons>
      </PageHeader>

      <SearchAndFilters>
        <SearchInput>
          <IconWrapper icon={FaSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search staff by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="pending">Pending</option>
        </FilterSelect>
        <FilterSelect value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="all">All Roles</option>
          {validRoles.map(role => (
            <option key={role} value={role}>{role}</option>
          ))}
        </FilterSelect>
      </SearchAndFilters>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Join Date</th>
              <th>Phone</th>
              <th>Assigned Projects</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentStaff.map((staff) => (
              <tr key={staff.id}>
                <td>
    <div>
                    <div style={{ fontWeight: '600', color: '#141340' }}>{staff.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>ID: {staff.id}</div>
                  </div>
                </td>
                <td>{staff.email}</td>
                <td>
                  <div style={{ fontWeight: '500', color: '#495057' }}>
                    {staff.role}
                  </div>
                </td>
                <td>
                  <StatusBadge status={staff.status}>
                    {staff.status}
                  </StatusBadge>
                </td>
                <td>{new Date(staff.joinDate).toLocaleDateString()}</td>
                <td>{staff.phone}</td>
                <td>
                  {staff.assignedProjects.length > 0 ? (
                    <div style={{ fontSize: '0.8rem' }}>
                      {staff.assignedProjects.join(', ')}
                    </div>
                  ) : (
                    <span style={{ color: '#666', fontSize: '0.8rem' }}>No projects</span>
                  )}
                </td>
                <td>
                  <ActionButtonsCell>
                    <ActionButton variant="view" title="View Details">
                      <IconWrapper icon={FaEye} />
                    </ActionButton>
                    <ActionButton variant="edit" title="Edit Staff" onClick={() => openEditModal(staff)}>
                      <IconWrapper icon={FaEdit} />
                    </ActionButton>
                    <ActionButton variant="assign" title="Assign to Project" onClick={() => openAssignModal(staff)}>
                      <IconWrapper icon={FaUsers} />
                    </ActionButton>
                    <ActionButton variant="delete" title="Delete Staff" onClick={() => handleDeleteStaff(staff.id)}>
                      <IconWrapper icon={FaTrash} />
                    </ActionButton>
                  </ActionButtonsCell>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        
        <Pagination>
          <PaginationInfo>
            Showing {startIndex + 1} to {Math.min(endIndex, filteredStaff.length)} of {filteredStaff.length} staff members
          </PaginationInfo>
          <PaginationButtons>
            <PaginationButton 
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <PaginationButton
                key={page}
                active={page === currentPage}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </PaginationButton>
            ))}
            <PaginationButton 
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
          </PaginationButtons>
        </Pagination>
      </TableContainer>

      {/* Add Staff Modal */}
      <ModalOverlay isOpen={isAddModalOpen}>
        <Modal>
          <ModalHeader>
            <ModalTitle>
              <IconWrapper icon={FaUserPlus} />
              Add New Staff
            </ModalTitle>
            <CloseButton onClick={() => setIsAddModalOpen(false)}>
              <IconWrapper icon={FaTimes} />
            </CloseButton>
          </ModalHeader>
          <Form onSubmit={handleAddStaff}>
            <FormRow>
              <FormGroup>
                <Label>Full Name *</Label>
                <Input
                  type="text"
                  value={newStaff.name}
                  onChange={(e) => setNewStaff({...newStaff, name: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                  required
                />
              </FormGroup>
            </FormRow>
            
            <FormRow>
              <FormGroup>
                <Label>Phone Number *</Label>
                <Input
                  type="tel"
                  value={newStaff.phone}
                  onChange={(e) => setNewStaff({...newStaff, phone: e.target.value})}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Role *</Label>
                <Select
                  value={newStaff.role}
                  onChange={(e) => setNewStaff({...newStaff, role: e.target.value})}
                  required
                >
                  <option value="">Select Role</option>
                  {validRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Join Date *</Label>
                <Input
                  type="date"
                  value={newStaff.joinDate}
                  onChange={(e) => setNewStaff({...newStaff, joinDate: e.target.value})}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Status</Label>
                <Select
                  value={newStaff.status}
                  onChange={(e) => setNewStaff({...newStaff, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="pending">Pending</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Address</Label>
              <TextArea
                value={newStaff.address}
                onChange={(e) => setNewStaff({...newStaff, address: e.target.value})}
                placeholder="Enter full address"
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Emergency Contact Name</Label>
                <Input
                  type="text"
                  value={newStaff.emergencyName}
                  onChange={(e) => setNewStaff({...newStaff, emergencyName: e.target.value})}
                />
              </FormGroup>
              <FormGroup>
                <Label>Emergency Contact Phone</Label>
                <Input
                  type="tel"
                  value={newStaff.emergencyContact}
                  onChange={(e) => setNewStaff({...newStaff, emergencyContact: e.target.value})}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Password Requirements</Label>
              <div style={{ 
                fontSize: '0.8rem', 
                color: '#666', 
                backgroundColor: '#f8f9fa', 
                padding: '0.75rem', 
                borderRadius: '6px',
                border: '1px solid #e0e0e0'
              }}>
                <div>• Minimum 6 characters required</div>
                <div>• Password will be sent to staff member's email</div>
                <div>• Staff can change password after first login</div>
    </div>
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={newStaff.password}
                  onChange={(e) => setNewStaff({...newStaff, password: e.target.value})}
                  placeholder="Enter password (min 6 characters)"
                  required
                />
                {newStaff.password && (
                  <PasswordStrength strength={getPasswordStrength(newStaff.password)}>
                    <IconWrapper icon={getPasswordStrength(newStaff.password) === 'strong' ? FaCheck : FaExclamationTriangle} />
                    {getPasswordStrengthText(getPasswordStrength(newStaff.password))}
                  </PasswordStrength>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Confirm Password *</Label>
                <Input
                  type="password"
                  value={newStaff.confirmPassword}
                  onChange={(e) => setNewStaff({...newStaff, confirmPassword: e.target.value})}
                  placeholder="Confirm password"
                  required
                />
                {newStaff.confirmPassword && (
                  <PasswordMatch matches={newStaff.password === newStaff.confirmPassword}>
                    <IconWrapper icon={newStaff.password === newStaff.confirmPassword ? FaCheck : FaExclamationTriangle} />
                    {newStaff.password === newStaff.confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                  </PasswordMatch>
                )}
              </FormGroup>
            </FormRow>

            <ModalActions>
              <Button variant="secondary" type="button" onClick={() => setIsAddModalOpen(false)}>
                <IconWrapper icon={FaTimes} />
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                <IconWrapper icon={FaSave} />
                {isSubmitting ? 'Adding...' : 'Add Staff'}
              </Button>
            </ModalActions>
          </Form>
        </Modal>
      </ModalOverlay>

      {/* Edit Staff Modal */}
      <ModalOverlay isOpen={isEditModalOpen}>
        <Modal>
          <ModalHeader>
            <ModalTitle>
              <IconWrapper icon={FaEdit} />
              Edit Staff
            </ModalTitle>
            <CloseButton onClick={() => setIsEditModalOpen(false)}>
              <IconWrapper icon={FaTimes} />
            </CloseButton>
          </ModalHeader>
          {selectedStaff && (
            <Form onSubmit={handleEditStaff}>
              <FormRow>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    type="text"
                    value={selectedStaff.name}
                    onChange={(e) => setSelectedStaff({...selectedStaff, name: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={selectedStaff.email}
                    onChange={(e) => setSelectedStaff({...selectedStaff, email: e.target.value})}
                    required
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    value={selectedStaff.phone}
                    onChange={(e) => setSelectedStaff({...selectedStaff, phone: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Role *</Label>
                  <Select
                    value={selectedStaff.role}
                    onChange={(e) => setSelectedStaff({...selectedStaff, role: e.target.value})}
                    required
                  >
                    <option value="">Select Role</option>
                    {validRoles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </Select>
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Join Date *</Label>
                  <Input
                    type="date"
                    value={selectedStaff.joinDate}
                    onChange={(e) => setSelectedStaff({...selectedStaff, joinDate: e.target.value})}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Salary (TZS)</Label>
                  <Input
                    type="number"
                    value={selectedStaff.salary}
                    onChange={(e) => setSelectedStaff({...selectedStaff, salary: e.target.value})}
                    placeholder="Enter salary amount"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Status</Label>
                  <Select
                    value={selectedStaff.status}
                    onChange={(e) => setSelectedStaff({...selectedStaff, status: e.target.value})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Address</Label>
                <TextArea
                  value={selectedStaff.address}
                  onChange={(e) => setSelectedStaff({...selectedStaff, address: e.target.value})}
                  placeholder="Enter full address"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Emergency Contact Name</Label>
                  <Input
                    type="text"
                    value={selectedStaff.emergencyName}
                    onChange={(e) => setSelectedStaff({...selectedStaff, emergencyName: e.target.value})}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Emergency Contact Phone</Label>
                  <Input
                    type="tel"
                    value={selectedStaff.emergencyContact}
                    onChange={(e) => setSelectedStaff({...selectedStaff, emergencyContact: e.target.value})}
                  />
                </FormGroup>
              </FormRow>

              <ModalActions>
                <Button variant="secondary" type="button" onClick={() => setIsEditModalOpen(false)}>
                  <IconWrapper icon={FaTimes} />
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  <IconWrapper icon={FaSave} />
                  {isSubmitting ? 'Updating...' : 'Update Staff'}
                </Button>
              </ModalActions>
            </Form>
          )}
        </Modal>
      </ModalOverlay>

      {/* Assign Project Modal */}
      <ModalOverlay isOpen={isAssignModalOpen}>
        <Modal>
          <ModalHeader>
            <ModalTitle>
              <IconWrapper icon={FaUsers} />
              Assign Staff to Project
            </ModalTitle>
            <CloseButton onClick={() => setIsAssignModalOpen(false)}>
              <IconWrapper icon={FaTimes} />
            </CloseButton>
          </ModalHeader>
          {selectedStaff && (
            <Form onSubmit={handleAssignProject}>
              <FormGroup>
                <Label>Staff Member</Label>
                <Input
                  type="text"
                  value={selectedStaff.name}
                  disabled
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Staff Role</Label>
                <Input
                  type="text"
                  value={selectedStaff.role}
                  disabled
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Select Project *</Label>
                <Select required>
                  <option value="">Choose a project</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.id} - {project.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Assignment Notes</Label>
                <TextArea
                  placeholder="Add any notes about this assignment..."
                />
              </FormGroup>

              <ModalActions>
                <Button variant="secondary" type="button" onClick={() => setIsAssignModalOpen(false)}>
                  <IconWrapper icon={FaTimes} />
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  <IconWrapper icon={FaSave} />
                  {isSubmitting ? 'Assigning...' : 'Assign to Project'}
                </Button>
              </ModalActions>
            </Form>
          )}
        </Modal>
      </ModalOverlay>
    </Container>
  );
};

export default HRStaffList; 